import { useTranslation } from 'react-i18next'
import { Radio, RadioGroup } from '@structural-climbing/ui'
import type { CompetitionCategoryView } from './types'

/**
 * Which category a member enters: who they are ranked against, as opposed to
 * the time slot that says when they climb.
 *
 * Every category is shown, including the ones this member cannot enter, with
 * the reason. Hiding them would leave someone wondering where the category
 * their friend entered went.
 */
export function CategoryPicker({
  categories,
  value,
  onChange,
}: {
  categories: CompetitionCategoryView[]
  value: string | null
  onChange: (id: string) => void
}) {
  const { t } = useTranslation()

  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={onChange}
      aria-label={t('competitions.chooseCategory')}
    >
      {categories.map((category) => (
        <Radio key={category.id} value={category.id} isDisabled={!category.eligible}>
          <span className="flex flex-col">
            <span className="text-sm font-medium">{category.name}</span>
            <span className="text-xs text-foreground/50">{describe(category, t)}</span>
            {category.description && (
              <span className="text-xs text-foreground/60">{category.description}</span>
            )}
            {!category.eligible && (
              <span className="text-xs text-warning">{blockedLabel(category, t)}</span>
            )}
          </span>
        </Radio>
      ))}
    </RadioGroup>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

/** "18 and over · Women · 4 seats left" — the rules, in the order they matter. */
function describe(category: CompetitionCategoryView, t: Translate): string {
  const parts: string[] = [ageLabel(category, t)]

  if (category.gender !== 'any') {
    parts.push(
      category.gender === 'male'
        ? t('competitions.categoryGenderMale')
        : t('competitions.categoryGenderFemale'),
    )
  }

  if (category.seats_left !== null) {
    parts.push(
      category.seats_left === 0
        ? t('competitions.categoryFull')
        : t('competitions.sessionSeatsLeft', { count: category.seats_left }),
    )
  }

  return parts.join(' · ')
}

function ageLabel(category: CompetitionCategoryView, t: Translate): string {
  const { min_age: min, max_age: max } = category
  if (min !== null && max !== null) return t('competitions.categoryAgeRange', { min, max })
  if (min !== null) return t('competitions.categoryAgeMin', { min })
  if (max !== null) return t('competitions.categoryAgeMax', { max })
  return t('competitions.categoryAgeAny')
}

function blockedLabel(category: CompetitionCategoryView, t: Translate): string {
  switch (category.blocked_reason) {
    case 'gender':
      return t('competitions.categoryBlockedGender')
    case 'age':
      return t('competitions.categoryBlockedAge')
    case 'profile_gender':
      return t('competitions.categoryNeedsGender')
    case 'profile_dob':
      return t('competitions.categoryNeedsDob')
    case 'full':
      return t('competitions.categoryFull')
    default:
      return ''
  }
}
